/**
 * Renovatieproject — aanmaakmodal.
 *
 * Eenvoudig modal-formulier dat alleen de minimaal nodige velden vraagt om
 * een project op te zetten:
 *  • Titel
 *  • Beschrijving (optioneel)
 *  • Geschat ruw budget (optioneel)
 *  • Verantwoordelijke (UniversalPickerModal in `assignee` mode — zelfde
 *    UX als helpdesk)
 *  • Voorziene kostenplaatsen (optioneel — eigen modal met "moet leeg"
 *    toggle, hergebruikt het patroon van de detailpagina-cel)
 *
 * Het project-id wordt server-side bepaald (RENO-N), dus we tonen het pas
 * na succesvol opslaan via een redirect naar /renovation/:id (gedrag van
 * `onCreated` hook is overlaatbaar via prop).
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  Cube,
  CurrencyEur,
  PencilSimple,
  User,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { useUsers } from "shared/contexts/users.context";
import { UnitDTO } from "shared/types/unit";
import { ProjectStatus } from "shared/types/renovation";

// ---------------------------------------------------------------------------
// Voorziene kostenplaatsen — picker dialog (draft-only, no persistence here)
// ---------------------------------------------------------------------------

interface DraftUnit {
  unitId: string;
  mustBeEmpty: boolean;
}

interface ProvisionalUnitsPickerProps {
  buildingId: string;
  isOpen: boolean;
  initial: DraftUnit[];
  onClose: () => void;
  onSave: (next: DraftUnit[]) => void;
}

const ProvisionalUnitsPicker = ({
  buildingId,
  isOpen,
  initial,
  onClose,
  onSave,
}: ProvisionalUnitsPickerProps) => {
  const { session } = useSession();
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const [draft, setDraft] = useState<DraftUnit[]>(initial);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft(initial);
      setSearch("");
    }
  }, [isOpen, initial]);

  useEffect(() => {
    if (!isOpen || !buildingId || !session) return;
    let cancelled = false;
    setIsLoading(true);
    fetchWithAuth(
      `${API_URL}/units/building/${buildingId}`,
      { method: "GET" },
      session,
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch units: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.data || [];
        setUnits(list);
      })
      .catch((err) => {
        console.error(
          "[ProvisionalUnitsPicker] failed to load units",
          err,
        );
        toast.error("Fout bij ophalen kostenplaatsen");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, buildingId, session]);

  const draftMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const d of draft) m.set(d.unitId, d.mustBeEmpty);
    return m;
  }, [draft]);

  const filteredUnits = useMemo(() => {
    const withIds = units.filter(
      (u): u is UnitDTO & { id: string } => !!u.id,
    );
    const term = search.trim().toLowerCase();
    if (!term) return withIds;
    return withIds.filter((u) => u.id.toLowerCase().includes(term));
  }, [units, search]);

  const toggle = (unitId: string) => {
    setDraft((prev) => {
      const exists = prev.some((d) => d.unitId === unitId);
      if (exists) return prev.filter((d) => d.unitId !== unitId);
      return [...prev, { unitId, mustBeEmpty: false }];
    });
  };

  const setMustBeEmpty = (unitId: string, value: boolean) => {
    setDraft((prev) =>
      prev.map((d) =>
        d.unitId === unitId ? { ...d, mustBeEmpty: value } : d,
      ),
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Voorziene kostenplaatsen</DialogTitle>
          <DialogDescription>
            Kies de units waarop dit project van toepassing is. Markeer een
            unit als "moet leeg" wanneer ze ontruimd dient te zijn voor de
            werken kunnen starten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Zoek op unit-id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="max-h-[400px] overflow-y-auto rounded-md border border-neutral-200">
            {isLoading ? (
              <div className="p-4 text-sm text-neutral-secondary-mid">
                Laden...
              </div>
            ) : filteredUnits.length === 0 ? (
              <div className="p-4 text-sm text-neutral-secondary-mid">
                Geen units gevonden.
              </div>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {filteredUnits.map((u) => {
                  const isSelected = draftMap.has(u.id);
                  const mustBeEmpty = draftMap.get(u.id) ?? false;
                  return (
                    <li
                      key={u.id}
                      className={`flex items-center gap-3 px-3 py-2 ${
                        isSelected ? "bg-moss-primary/5" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(u.id)}
                        className="flex flex-1 items-center gap-2 text-left"
                      >
                        <Cube
                          className={`h-4 w-4 ${
                            isSelected
                              ? "text-moss-primary"
                              : "text-neutral-secondary-mid"
                          }`}
                          weight="duotone"
                        />
                        <span className="text-sm font-medium text-neutral-primary">
                          {u.id}
                        </span>
                      </button>
                      {isSelected && (
                        <label className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
                          Moet leeg
                          <Switch
                            checked={mustBeEmpty}
                            onCheckedChange={(v) =>
                              setMustBeEmpty(u.id, v)
                            }
                          />
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button
            onClick={() => {
              onSave(draft);
              onClose();
            }}
          >
            {draft.length} {draft.length === 1 ? "unit" : "units"} bewaren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface NewRenovationProjectModalProps {
  isOpen: boolean;
  buildingId: string;
  onClose: () => void;
  /**
   * Called when the project is successfully created. By default the modal
   * navigates to /renovation/:id; pass a custom handler to keep the user on
   * the current page (e.g. to refresh a list in place) — return `false` to
   * suppress the default navigation.
   */
  onCreated?: (project: { id: number | string }) => void | boolean;
}

export const NewRenovationProjectModal = ({
  isOpen,
  buildingId,
  onClose,
  onCreated,
}: NewRenovationProjectModalProps) => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { user } = useUser();
  const { users } = useUsers();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [roughBudget, setRoughBudget] = useState("");
  const [responsibleUserId, setResponsibleUserId] = useState<string | null>(
    user?.id ?? null,
  );
  const [provisionalUnits, setProvisionalUnits] = useState<DraftUnit[]>([]);

  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [unitsPickerOpen, setUnitsPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Reset state every time the modal opens — start clean.
  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setDescription("");
    setRoughBudget("");
    setResponsibleUserId(user?.id ?? null);
    setProvisionalUnits([]);
  }, [isOpen, user?.id]);

  const responsibleUser = useMemo(
    () => users.find((u) => u.id === responsibleUserId) || null,
    [users, responsibleUserId],
  );

  const canSubmit =
    title.trim().length > 0 &&
    !!buildingId &&
    !!responsibleUserId &&
    !isSaving;

  const handleSubmit = async () => {
    if (!session) {
      toast.error("Geen actieve sessie");
      return;
    }
    if (!buildingId) {
      toast.error("Geen gebouw geselecteerd");
      return;
    }
    if (!title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    if (!responsibleUserId) {
      toast.error("Verantwoordelijke is verplicht");
      return;
    }

    try {
      setIsSaving(true);

      const parsedBudget =
        roughBudget.trim() === "" ? null : Number(roughBudget);
      if (parsedBudget !== null && Number.isNaN(parsedBudget)) {
        throw new Error("Geschat ruw budget is geen geldig getal");
      }

      const createRes = await fetchWithAuth(
        `${API_URL}/renovation/project`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim() || undefined,
            buildingId,
            unitIds: [],
            responsibleUserId,
            status: ProjectStatus.DESIGN,
            roughBudgetEstimate: parsedBudget,
          }),
        },
        session,
      );

      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.message || "Project aanmaken mislukt");
      }

      const project = await createRes.json();

      // Persist voorziene kostenplaatsen in a follow-up call when the user
      // picked any. The dedicated endpoint takes the full draft list so we
      // don't need to round-trip per unit.
      if (provisionalUnits.length > 0) {
        const unitsRes = await fetchWithAuth(
          `${API_URL}/renovation/project/${project.id}/provisional-units`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ units: provisionalUnits }),
          },
          session,
        );
        if (!unitsRes.ok) {
          const err = await unitsRes.json().catch(() => ({}));
          // Project did get created; surface the units error but still navigate.
          console.error(
            "[NewRenovationProjectModal] units PUT failed",
            err,
          );
          toast.warn(
            "Project aangemaakt, maar voorziene kostenplaatsen niet opgeslagen — corrigeer op de detailpagina.",
          );
        }
      }

      toast.success("Project aangemaakt");
      onClose();

      const result = onCreated?.(project);
      if (result === false) return;
      navigate(`/renovation/${project.id}`);
    } catch (err) {
      console.error("[NewRenovationProjectModal] create failed", err);
      toast.error(
        err instanceof Error ? err.message : "Fout bij aanmaken project",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && !isSaving) onClose();
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Nieuw renovatieproject</DialogTitle>
            <DialogDescription>
              Vul de basisgegevens in. De rest kan je later aanvullen op de
              detailpagina.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="project-title">
                Titel <span className="text-red-600">*</span>
              </Label>
              <Input
                id="project-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bv. Renovatie badkamer appartement 3"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-description">
                Beschrijving{" "}
                <span className="text-xs text-neutral-secondary-mid">
                  (optioneel)
                </span>
              </Label>
              <Textarea
                id="project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Korte toelichting over de scope van dit project..."
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-budget">
                Geschat ruw budget{" "}
                <span className="text-xs text-neutral-secondary-mid">
                  (optioneel, excl. btw)
                </span>
              </Label>
              <div className="relative">
                <CurrencyEur
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-secondary-mid"
                  weight="duotone"
                />
                <Input
                  id="project-budget"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={roughBudget}
                  onChange={(e) => setRoughBudget(e.target.value)}
                  placeholder="0"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>
                Verantwoordelijke <span className="text-red-600">*</span>
              </Label>
              <button
                type="button"
                onClick={() => setUserPickerOpen(true)}
                className="flex w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-3 py-2 text-left text-sm hover:border-moss-primary/50 hover:bg-moss-primary/5 transition-colors"
              >
                {responsibleUser ? (
                  <span className="flex items-center gap-2">
                    <User
                      className="h-4 w-4 text-moss-primary"
                      weight="duotone"
                    />
                    <span className="font-medium text-neutral-primary">
                      {responsibleUser.first_name}{" "}
                      {responsibleUser.last_name}
                    </span>
                    {responsibleUser.email && (
                      <span className="text-xs text-neutral-secondary-mid">
                        {responsibleUser.email}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-sm text-neutral-secondary-mid">
                    Selecteer verantwoordelijke...
                  </span>
                )}
                <PencilSimple className="h-4 w-4 text-neutral-secondary-mid" />
              </button>
            </div>

            <div className="space-y-1.5">
              <Label>
                Voorziene kostenplaatsen{" "}
                <span className="text-xs text-neutral-secondary-mid">
                  (optioneel)
                </span>
              </Label>
              <button
                type="button"
                onClick={() => setUnitsPickerOpen(true)}
                className="flex w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-3 py-2 text-left text-sm hover:border-moss-primary/50 hover:bg-moss-primary/5 transition-colors"
              >
                {provisionalUnits.length === 0 ? (
                  <span className="text-sm text-neutral-secondary-mid">
                    Geen kostenplaatsen geselecteerd
                  </span>
                ) : (
                  <span className="flex flex-wrap items-center gap-1.5">
                    {provisionalUnits.map((u) => (
                      <span
                        key={u.unitId}
                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${
                          u.mustBeEmpty
                            ? "border-amber-300 bg-amber-50 text-amber-800"
                            : "border-moss-primary/30 bg-moss-primary/10 text-moss-primary"
                        }`}
                      >
                        <Cube className="h-3 w-3" weight="duotone" />
                        {u.unitId}
                        {u.mustBeEmpty && (
                          <span className="text-[9px] uppercase tracking-wide">
                            leeg
                          </span>
                        )}
                      </span>
                    ))}
                  </span>
                )}
                <PencilSimple className="h-4 w-4 text-neutral-secondary-mid shrink-0" />
              </button>
              {provisionalUnits.length > 0 && (
                <button
                  type="button"
                  onClick={() => setProvisionalUnits([])}
                  className="inline-flex items-center gap-1 text-xs text-neutral-secondary-mid hover:text-red-600"
                >
                  <X className="h-3 w-3" weight="bold" />
                  Wis selectie
                </button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Annuleren
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {isSaving ? "Bezig met opslaan..." : "Project aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UniversalPickerModal
        isOpen={userPickerOpen}
        mode="assignee"
        title="Selecteer verantwoordelijke"
        onClose={() => setUserPickerOpen(false)}
        onConfirm={(value) => {
          setUserPickerOpen(false);
          if (value && typeof value === "object" && "id" in value) {
            setResponsibleUserId((value as { id: string }).id);
          }
        }}
        config={{
          defaultValue: responsibleUser,
          allowRemoval: false,
        }}
      />

      <ProvisionalUnitsPicker
        buildingId={buildingId}
        isOpen={unitsPickerOpen}
        initial={provisionalUnits}
        onClose={() => setUnitsPickerOpen(false)}
        onSave={(next) => setProvisionalUnits(next)}
      />
    </>
  );
};
